import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../../../services/notification.service';
import { TunnelService } from '../../../../../services/tunnel.service';
import { TunnelPackage, TunnelStatus } from '../../../../../models/tunnel.model';
import {
  TUNNEL_NAME_MAX_LENGTH,
  TUNNEL_PULSE_MS_DEFAULT,
  TUNNEL_PULSE_MS_MAX,
  TUNNEL_PULSE_MS_MIN,
  TUNNEL_RELAY_MAX,
  TUNNEL_RELAY_MIN,
} from '../../../../../constants/tunnel.constants';
import {
  TunnelPackageFormErrors,
  apiErrorMessage,
  buildTunnelPackagePayload,
  hasFormErrors,
  mapTunnelPackageToForm,
  validateTunnelPackageForm,
} from '../../../../../utils/tunnel.util';

const EMPTY_ERRORS: TunnelPackageFormErrors = { name: '', relay: '', pulseMs: '' };

/** Add or edit one tunnel wash package (the c-relay pulsed for that wash). */
@Component({
  selector: 'app-tunnel-package-form-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tunnel-package-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../lanes/lane-form-modal/lane-form-modal.component.scss'],
})
export class TunnelPackageFormModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() companyId = '';
  @Input() locationId = '';
  /** The package being edited; null adds one. */
  @Input() tunnelPackage: TunnelPackage | null = null;
  /** The tunnel's packages, for the unique name / relay check. */
  @Input() existingPackages: TunnelPackage[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly nameMaxLength = TUNNEL_NAME_MAX_LENGTH;
  readonly relayMin = TUNNEL_RELAY_MIN;
  readonly relayMax = TUNNEL_RELAY_MAX;
  readonly pulseMin = TUNNEL_PULSE_MS_MIN;
  readonly pulseMax = TUNNEL_PULSE_MS_MAX;
  readonly pulseDefault = TUNNEL_PULSE_MS_DEFAULT;

  name = '';
  relay = '';
  pulseMs = String(TUNNEL_PULSE_MS_DEFAULT);
  status: TunnelStatus = 'active';
  errors: TunnelPackageFormErrors = { ...EMPTY_ERRORS };
  isSubmitting = false;

  /**
   * Bumped whenever the modal opens, closes or changes location, so a save that
   * finishes afterwards cannot reload or close the modal of another location.
   */
  private openSeq = 0;
  private readonly tunnelService = inject(TunnelService);
  private readonly messageService = inject(NotificationService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['companyId'] || changes['locationId']) {
      this.openSeq += 1;
    }
    if (changes['isOpen']?.currentValue) {
      const form = mapTunnelPackageToForm(this.tunnelPackage);
      this.name = form.name;
      this.relay = form.relay;
      this.pulseMs = form.pulseMs;
      this.status = this.tunnelPackage?.status ?? 'active';
      this.errors = { ...EMPTY_ERRORS };
      this.isSubmitting = false;
    }
  }

  get isEdit(): boolean {
    return this.tunnelPackage !== null;
  }

  get modalTitle(): string {
    return this.isEdit ? 'Edit tunnel wash package' : 'Add tunnel wash package';
  }

  get submitLabel(): string {
    return this.isEdit ? 'Save changes' : 'Add package';
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onClose(): void {
    if (!this.isSubmitting) {
      this.close.emit();
    }
  }

  onSubmit(): void {
    if (this.isSubmitting || !this.companyId || !this.locationId) {
      return;
    }
    const values = { name: this.name, relay: this.relay, pulseMs: this.pulseMs };
    // An inactive package frees its name and relay, so only check clashes when active.
    const checkAgainst = this.status === 'active' ? this.existingPackages : [];
    this.errors = validateTunnelPackageForm(values, checkAgainst, this.tunnelPackage?.id ?? null);
    if (hasFormErrors(this.errors)) {
      return;
    }

    const payload = buildTunnelPackagePayload(values);
    const request$ = this.tunnelPackage
      ? this.tunnelService.updateTunnelPackage(
          this.companyId,
          this.locationId,
          this.tunnelPackage.id,
          { ...payload, status: this.status }
        )
      : this.tunnelService.createTunnelPackage(this.companyId, this.locationId, payload);

    this.isSubmitting = true;
    const seq = this.openSeq;
    request$.subscribe({
      next: response => {
        this.isSubmitting = false;
        if (seq !== this.openSeq) {
          return;
        }
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.isEdit ? 'Tunnel wash package saved' : 'Tunnel wash package added',
          });
          this.saved.emit();
          this.close.emit();
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response.message || 'Failed to save the tunnel wash package',
        });
      },
      error: err => {
        this.isSubmitting = false;
        if (seq !== this.openSeq) {
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: apiErrorMessage(err, 'Failed to save the tunnel wash package'),
        });
      },
    });
  }
}
